import { PNG } from 'pngjs/browser';

export interface DecodedImage {
  width: number;
  height: number;
  data: Uint8Array;
  /** True when the photo was larger than the decode limit and these pixels are a reduced copy of it. */
  downscaled?: boolean;
}

type AnyCanvas = OffscreenCanvas | HTMLCanvasElement;

function makeCanvas(width: number, height: number): AnyCanvas {
  if (typeof OffscreenCanvas !== 'undefined') {
    return new OffscreenCanvas(width, height);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

async function loadBitmap(url: string, fetchedBlob?: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap !== 'undefined' && typeof fetch !== 'undefined') {
    try {
      if (fetchedBlob) return await createImageBitmap(fetchedBlob);
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await createImageBitmap(await response.blob());
    } catch {
      // WebKit can fail reading a blob: URL body (NotReadableError) or decoding the blob; an <img> still decodes it.
    }
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const local = fetchedBlob ? URL.createObjectURL(fetchedBlob) : undefined;
    img.crossOrigin = 'anonymous';
    img.onload = (): void => {
      if (local) URL.revokeObjectURL(local);
      resolve(img);
    };

    img.onerror = (error): void => {
      if (local) URL.revokeObjectURL(local);
      reject(error);
    };

    img.src = local ?? url;
  });
}

function dimensions(image: ImageBitmap | HTMLImageElement): { width: number; height: number } {
  if ('naturalWidth' in image) {
    return { width: image.naturalWidth, height: image.naturalHeight };
  }

  return { width: image.width, height: image.height };
}

/**
 * Longest side a decode keeps by default. The rendition ladder never climbs above 1920, so only a photo without
 * renditions reaches this: its original is several times larger, and at full size a few of them exhaust the
 * wasm renderer's memory. The scene places a photo by its frame, not by its pixels, so a smaller decode only
 * softens it.
 */
export const DEFAULT_DECODE_MAX_LONG_EDGE = 4096;

export interface DecodeImageOptions {
  /** Known authored PNGs retain straight-alpha samples, without canvas/profile conversion or resizing. */
  preservePngRgba?: boolean;
  /** Longest side of the decoded bitmap; a larger photo is scaled down to it, a smaller one is never scaled up. */
  maxLongEdge?: number;
}

export function imageDecodeVersion(url: string, preservePngRgba?: boolean): string {
  return preservePngRgba ? `png-rgba:${url}` : url;
}

/** Size a `width × height` photo decodes to under `maxLongEdge`: aspect kept, never upscaled, at least 1 px. */
export function fitLongEdge(width: number, height: number, maxLongEdge: number): { width: number; height: number } {
  const longEdge = Math.max(width, height);
  if (longEdge <= maxLongEdge) return { width, height };
  const scale = maxLongEdge / longEdge;
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

// Same-origin blob: URLs (from CanvasImageCacheService) avoid canvas taint; output is straight-alpha RGBA for the Rust image map.
export async function decodeImageForRust(url: string, options?: DecodeImageOptions): Promise<DecodedImage> {
  let fetchedBlob: Blob | undefined;
  if (options?.preservePngRgba) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    fetchedBlob = await response.blob();
    const bytes = new Uint8Array(await fetchedBlob.arrayBuffer());
    if ([137, 80, 78, 71, 13, 10, 26, 10].every((v, i) => bytes[i] === v)) {
      return new Promise((resolve, reject) => {
        // pngjs' browser stream accepts Uint8Array and creates its own bundled Buffer; no Node globals required.
        new PNG().parse(bytes as Parameters<PNG['parse']>[0], (error, decoded) => {
          if (error) reject(error);
          else resolve({ width: decoded.width, height: decoded.height, data: new Uint8Array(decoded.data), downscaled: false });
        });
      });
    }
  }

  const image = await loadBitmap(url, fetchedBlob);
  const natural = dimensions(image);
  const { width, height } = fitLongEdge(natural.width, natural.height, options?.maxLongEdge ?? DEFAULT_DECODE_MAX_LONG_EDGE);

  const canvas = makeCanvas(width, height);
  const ctx = canvas.getContext('2d', { willReadFrequently: true }) as
    | CanvasRenderingContext2D
    | OffscreenCanvasRenderingContext2D
    | null;
  if (!ctx) throw new Error('2d context unavailable for image decode');

  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(image as CanvasImageSource, 0, 0, width, height);
  const { data } = ctx.getImageData(0, 0, width, height);

  if ('close' in image) image.close();

  return { width, height, data: new Uint8Array(data), downscaled: width !== natural.width || height !== natural.height };
}
